import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  Typography,
  message,
  Tag,
  Spin,
  Alert,
  InputNumber,
  Popconfirm,
  Tooltip
} from 'antd';
import {
  CheckCircleOutlined,
  LinkOutlined,
  DisconnectOutlined,
  InboxOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { customerOrderAPI } from '../services/api.ts';
import {
  CustomerOrder,
  StockOrderCoverage,
  StockOrderCoverageLine,
  StockOrderLoad,
  StockOrderCandidate
} from '../types.ts';

const { Title, Text } = Typography;

interface StockOrderCoverageModalProps {
  visible: boolean;
  order: CustomerOrder | null;
  onClose: () => void;
  /** Richiamato dopo ogni modifica della copertura (per aggiornare l'evadibilità in lista) */
  onCoverageChanged?: () => void;
}

/**
 * Copertura di un ORDINE STOCK: quanta merce è ENTRATA in magazzino a fronte
 * dell'ordine, riga per riga.
 *
 * La copertura è storica: resta acquisita anche se la merce entrata viene poi
 * venduta (l'ordine stock ha comunque assolto il suo scopo). Si alimenta da sola
 * ai carichi (IM7/IM4, DDT, giacenza collegata dall'Inventario) e si può
 * completare a mano collegando giacenze arrivate per altre strade.
 */
const StockOrderCoverageModal: React.FC<StockOrderCoverageModalProps> = ({
  visible,
  order,
  onClose,
  onCoverageChanged
}) => {
  const [loading, setLoading] = useState(false);
  const [coverage, setCoverage] = useState<StockOrderCoverage | null>(null);

  // Collegamento manuale
  const [linkingLine, setLinkingLine] = useState<StockOrderCoverageLine | null>(null);
  const [candidates, setCandidates] = useState<StockOrderCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [quantities, setQuantities] = useState<{ [stockId: number]: number }>({});
  const [remaining, setRemaining] = useState(0);

  const loadCoverage = useCallback(async () => {
    if (!order) return;
    setLoading(true);
    try {
      const data = await customerOrderAPI.getStockLoads(order.id);
      setCoverage(data);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nel caricamento della copertura');
    } finally {
      setLoading(false);
    }
  }, [order]);

  useEffect(() => {
    if (visible && order) {
      loadCoverage();
      setLinkingLine(null);
      setCandidates([]);
    }
  }, [visible, order, loadCoverage]);

  const openCandidates = async (line: StockOrderCoverageLine) => {
    if (!order) return;
    setLinkingLine(line);
    setCandidatesLoading(true);
    setQuantities({});
    try {
      const data = await customerOrderAPI.getStockLoadCandidates(order.id, line.product_id);
      setCandidates(data.candidates || []);
      setRemaining(data.remaining_quantity || 0);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nel caricamento delle giacenze');
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const handleLink = async (candidate: StockOrderCandidate) => {
    if (!order) return;
    const quantity = quantities[candidate.stock_id] ?? Math.min(candidate.attributable_quantity, remaining);
    if (!quantity || quantity <= 0) {
      message.warning('Indicare una quantità maggiore di zero');
      return;
    }
    try {
      const res = await customerOrderAPI.createStockLoad(order.id, {
        stock_id: candidate.stock_id,
        quantity
      });
      message.success(res.message || 'Giacenza collegata');
      await loadCoverage();
      if (linkingLine) await openCandidates(linkingLine);
      onCoverageChanged?.();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nel collegamento della giacenza');
    }
  };

  const handleUnlink = async (load: StockOrderLoad) => {
    if (!order) return;
    try {
      await customerOrderAPI.deleteStockLoad(order.id, load.id);
      message.success('Attribuzione scollegata');
      await loadCoverage();
      if (linkingLine) await openCandidates(linkingLine);
      onCoverageChanged?.();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nello scollegamento');
    }
  };

  const frozen = coverage?.is_frozen ?? false;

  const lineColumns = [
    {
      title: 'Prodotto',
      key: 'product',
      render: (record: StockOrderCoverageLine) => (
        <div>
          <Text strong>{record.product_code}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.product_description}</Text>
        </div>
      ),
    },
    {
      title: 'Richiesto',
      dataIndex: 'ordered_quantity',
      key: 'ordered_quantity',
      align: 'right' as const,
      width: 110,
    },
    {
      title: 'Entrato',
      dataIndex: 'loaded_quantity',
      key: 'loaded_quantity',
      align: 'right' as const,
      width: 110,
      render: (value: number) => <Text strong>{value}</Text>,
    },
    {
      title: 'Mancante',
      dataIndex: 'missing_quantity',
      key: 'missing_quantity',
      align: 'right' as const,
      width: 110,
      render: (value: number) => value > 0 ? <Text type="danger">{value}</Text> : <Text type="secondary">0</Text>,
    },
    {
      title: 'Stato',
      key: 'state',
      width: 130,
      align: 'center' as const,
      render: (record: StockOrderCoverageLine) => (
        record.is_complete
          ? <Tag color="green" icon={<CheckCircleOutlined />}>COMPLETA</Tag>
          : <Tag color="orange">{Math.round(record.completion_percentage)}%</Tag>
      ),
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 180,
      render: (record: StockOrderCoverageLine) => (
        <Button
          size="small"
          type={record.is_complete ? 'default' : 'primary'}
          icon={<LinkOutlined />}
          disabled={frozen}
          onClick={() => openCandidates(record)}
        >
          Collega giacenza
        </Button>
      ),
    },
  ];

  const loadColumns = [
    {
      title: 'Prodotto',
      dataIndex: 'product_code',
      key: 'product_code',
      width: 140,
    },
    {
      title: 'Quantità',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right' as const,
      width: 100,
    },
    {
      title: 'Giacenza',
      key: 'stock',
      width: 220,
      render: (record: StockOrderLoad) => (
        record.stock_id ? (
          <div>
            <Text>#{record.stock_id}{record.stock_code ? ` · ${record.stock_code}` : ''}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.warehouse_description || '-'}
              {(record.udc_pallet || record.udc_collo) ? ` · UDC ${record.udc_pallet || ''} ${record.udc_collo || ''}` : ''}
            </Text>
          </div>
        ) : <Text type="secondary">-</Text>
      ),
    },
    {
      title: 'Origine',
      key: 'source',
      width: 200,
      render: (record: StockOrderLoad) => <Tag>{record.source_label}</Tag>,
    },
    {
      title: 'Prenotata da altri',
      key: 'other_reservations',
      width: 190,
      render: (record: StockOrderLoad) => (
        !record.other_reservations || record.other_reservations.length === 0
          ? <Text type="secondary">-</Text>
          : (
            <Tooltip title="La giacenza è anche prenotata da altri ordini: la copertura resta valida (registro storico), ma i pezzi potrebbero uscire dal magazzino">
              <Space direction="vertical" size={0}>
                {record.other_reservations.map((o, i) => (
                  <Tag color="orange" key={i}>{o.order_number} ({o.qty})</Tag>
                ))}
              </Space>
            </Tooltip>
          )
      ),
    },
    {
      title: 'Data ingresso',
      dataIndex: 'loaded_at',
      key: 'loaded_at',
      width: 130,
      render: (value?: string) => value ? dayjs(value).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Utente',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 110,
      render: (value?: string) => value || '-',
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 120,
      render: (record: StockOrderLoad) => (
        <Popconfirm
          title="Scollegare questa attribuzione?"
          description="La copertura della riga verrà ridotta della quantità indicata."
          onConfirm={() => handleUnlink(record)}
          okText="Sì"
          cancelText="No"
          disabled={frozen}
        >
          <Button size="small" danger icon={<DisconnectOutlined />} disabled={frozen}>
            Scollega
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const candidateColumns = [
    {
      title: 'Giacenza',
      key: 'stock',
      render: (record: StockOrderCandidate) => (
        <div>
          <Text strong>#{record.stock_id}{record.stock_code ? ` · ${record.stock_code}` : ''}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.warehouse_description || '-'}
            {(record.udc_pallet || record.udc_collo) ? ` · UDC ${record.udc_pallet || ''} ${record.udc_collo || ''}` : ''}
            {record.customer_company ? ` · ${record.customer_company}` : ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Data ingresso',
      dataIndex: 'date_stock',
      key: 'date_stock',
      width: 120,
      render: (value?: string) => value ? dayjs(value).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Disponibile',
      dataIndex: 'qta_available',
      key: 'qta_available',
      align: 'right' as const,
      width: 110,
    },
    {
      title: 'Attribuibile',
      key: 'attributable_quantity',
      align: 'right' as const,
      width: 130,
      render: (record: StockOrderCandidate) => (
        <Tooltip title="Disponibile meno le quantità già attribuite a un ordine stock">
          <Text strong={record.attributable_quantity > 0} type={record.attributable_quantity > 0 ? undefined : 'secondary'}>
            {record.attributable_quantity}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Già usata da',
      key: 'other_orders',
      width: 190,
      render: (record: StockOrderCandidate) => (
        record.other_orders.length === 0
          ? <Text type="secondary">-</Text>
          : (
            <Space direction="vertical" size={0}>
              {record.other_orders.map((o, i) => (
                <Tag color="orange" key={i}>{o.order_number} ({o.quantity})</Tag>
              ))}
            </Space>
          )
      ),
    },
    {
      title: 'Quantità',
      key: 'quantity',
      width: 130,
      render: (record: StockOrderCandidate) => (
        <InputNumber
          min={0}
          max={Math.min(record.attributable_quantity, remaining)}
          value={quantities[record.stock_id] ?? Math.min(record.attributable_quantity, remaining)}
          onChange={(value) => setQuantities({ ...quantities, [record.stock_id]: Number(value) || 0 })}
          disabled={record.attributable_quantity <= 0 || remaining <= 0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (record: StockOrderCandidate) => (
        <Button
          size="small"
          type="primary"
          icon={<LinkOutlined />}
          disabled={record.attributable_quantity <= 0 || remaining <= 0}
          onClick={() => handleLink(record)}
        >
          Collega
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <InboxOutlined style={{ marginRight: 8 }} />
            Copertura Ordine Stock
          </Title>
          {order && (
            <Text type="secondary">
              Ordine: {order.order_number} | Cliente: {order.customer?.company || 'N/A'}
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="reload" icon={<ReloadOutlined />} onClick={loadCoverage}>Aggiorna</Button>,
        <Button key="close" onClick={onClose}>Chiudi</Button>,
      ]}
      width={1200}
      style={{ top: 20 }}
    >
      <Spin spinning={loading}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Come si evade un ordine stock"
          description={
            <span>
              L'ordine è evadibile quando tutta la merce richiesta è <strong>entrata</strong> in
              magazzino a fronte dell'ordine. La copertura è storica: resta acquisita anche se la
              merce entrata viene poi venduta. Si aggiorna da sola ai carichi (H2/H1, DDT,
              giacenza collegata dall'Inventario); usare "Collega giacenza" per la merce arrivata
              per altre strade (trasferimenti da altri magazzini, giacenze già presenti).
            </span>
          }
        />

        {coverage && (
          <Space style={{ marginBottom: 16 }}>
            <Text strong>Copertura complessiva:</Text>
            <Tag color={coverage.is_complete ? 'green' : 'orange'}>
              {Math.round(coverage.total_percentage)}%
            </Tag>
            {coverage.is_complete
              ? <Tag color="green" icon={<CheckCircleOutlined />}>PRONTO PER EVASIONE</Tag>
              : <Tag color="orange">MERCE MANCANTE</Tag>}
            {coverage.is_frozen && (
              <Tag color="blue">ORDINE EVASO — copertura congelata</Tag>
            )}
          </Space>
        )}

        <Table
          columns={lineColumns}
          dataSource={coverage?.lines || []}
          rowKey="product_id"
          size="small"
          pagination={false}
          scroll={{ y: 260 }}
        />

        {linkingLine && (
          <div style={{ marginTop: 20 }}>
            <Space style={{ marginBottom: 8 }}>
              <Text strong>Giacenze collegabili a {linkingLine.product_code}</Text>
              <Tag color={remaining > 0 ? 'orange' : 'green'}>Residuo scoperto: {remaining}</Tag>
              <Button size="small" onClick={() => setLinkingLine(null)}>Chiudi elenco</Button>
            </Space>
            <Spin spinning={candidatesLoading}>
              <Table
                columns={candidateColumns}
                dataSource={candidates}
                rowKey="stock_id"
                size="small"
                pagination={false}
                scroll={{ y: 240 }}
                locale={{ emptyText: 'Nessuna giacenza disponibile per questo prodotto' }}
              />
            </Spin>
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <Text strong>Merce entrata a fronte dell'ordine</Text>
          <Table
            style={{ marginTop: 8 }}
            columns={loadColumns}
            dataSource={coverage?.loads || []}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ y: 260 }}
            locale={{ emptyText: 'Nessuna merce ancora attribuita a questo ordine' }}
          />
        </div>
      </Spin>
    </Modal>
  );
};

export default StockOrderCoverageModal;
